import type { APIDesignerStore } from '..';
import type { HttpMethods } from 'oas/types';
import type { OpenAPIV3 } from 'openapi-types';
import type { StateCreator } from 'zustand';

import { codes } from '@readme/http-status-codes';
import { generateJSONSchemaFromObject, type $TSFixMe } from '@readme/iso';
import Oas from 'oas';

import { actionLog } from '@core/store/util';
import { safelyStringifyJSON } from '@core/utils/json';

import { isTextBasedMIMEType, createEmptySchema } from '../BodySchemaEditor/utils';

import { parseResponseExampleJSON } from './utils';

interface AugmentedResponseExample {
  /**
   * Should be a concatenation of the status code and the summary (i.e., `namedId`).
   *
   * If the summary doesn't exist (e.g., if a single example is documented using the `example` object),
   * it will just be the status code.
   *
   * @example '200_example1'
   * @example '200'
   */
  id: string;

  /**
   * The media type of the example.
   *
   * @example 'application/json'
   */
  mediaType: string;

  /**
   * The summary (e.g., the example key in the `examples` object`) of the example.
   *
   * If the example is documented using the `example` object, this will be `undefined`.
   *
   * @example 'example1'
   */
  namedId?: string;

  /**
   * The status code of the example.
   *
   * @example '200'
   */
  status: string;
}

export interface ResponseExampleEditorSliceState {
  /**
   * The last edited response example. Instead of using selectedExample, we use this to track the last edited example.
   * This is useful for triggering UI re-renders and may be useful to track changes
   */
  lastEditedExample: AugmentedResponseExample | null;

  /**
   * Tracks which example card is currently open and optionally displays an error.
   */
  openedExample: {
    statusCode: string;
    contentType: string;
    namedId: string;
    error?: string;
  } | null;
}
export interface MediaTypeExample {
  description?: string;
  summary?: string;
  title?: string;
  value: unknown;
}

export interface ResponseExampleEditorSliceActions {
  /**
   * Adds a custom response example to the current operation.
   * Returns the namedId of the added example.
   */
  addResponseExample: (statusCode: string, specificMimeType: string) => string;

  /**
   * Changes the content type of the currently selected example.
   * Returns the new example information for auto-opening.
   */
  changeContentType: (
    newContentType: string,
    oldContentType: string,
    statusCode: string,
    namedId: string,
  ) => { statusCode: string; namedId: string; contentType: string };

  /**
   * Deletes the currently selected example.
   */
  deleteResponseExample: (statusCode: string, mediaType: string, namedId: string) => void;

  /**
   * Generates a response schema from the currently selected example.
   */
  generateResponseSchema: (args: {
    /**
     * The status code of the example.
     */
    statusCode: string;
    /**
     * The media type of the example.
     */
    mediaType: string;
    /**
     * The named ID of the example.
     */
    namedId?: string;
  }) => void;

  /**
   * Returns a map of status codes to media types to example names.
   * Format: Map<STATUS_CODE, Map<MEDIA_TYPE, EXAMPLE_NAMES>>
   *
   */
  getAllExampleNames: () => Map<string, Map<string, string[]>>;

  /**
   * Returns all the status codes that have actual response schemas
   */
  getAllValidStatusCodes: () => string[];

  /**
   * Returns the code of the example with the given status code, media type, and named ID.
   * If namedId is not provided, returns the first available example.
   */
  getExampleCode: (statusCode: string, mediaType: string, namedId?: string) => { code: string; namedId: string };

  /**
   * Returns the first example in the operation.
   */
  getFirstExample: () => AugmentedResponseExample;

  /**
   * Returns the response example properties on the operation object.
   */
  getOperationResponseProperties: (
    contentType: string,
    statusCode: string,
  ) => {
    hasContentType: boolean;
    hasCustomDescription: boolean;
    hasExamplesObject: boolean;
    hasMultipleContentTypes: boolean;
    hasMultipleExamples: boolean;
    hasResponseObject: boolean;
    hasStatusCode: boolean;
  };

  /**
   * Checks if a response has examples and returns the named IDs of all examples.
   * Returns null if no examples exist.
   */
  getResponseExampleNames: (statusCode: string, mediaType: string) => string[] | null;

  initialize: () => void;

  /**
   * Sets which example card is currently open.
   */
  setOpenedExample: (example: { statusCode: string; contentType: string; namedId: string } | null) => void;

  /**
   * Updates the name of the currently selected example.
   */
  updateResponseExampleName: (exampleName: string, statusCode: string, mediaType: string, namedId: string) => void;

  /**
   * Updates the code of the currently selected example.
   */
  updateSnippet: (value: string, statusCode: string, mediaType: string, namedId: string) => void;
}

export interface ResponseExampleEditorSlice {
  /**
   * State slice containing fields and actions that pertain to the Response Example Editor
   */
  responseExampleEditor: ResponseExampleEditorSliceActions & ResponseExampleEditorSliceState;
}

export const initialState: ResponseExampleEditorSliceState = {
  lastEditedExample: null,
  openedExample: null,
};

/**
 * Response Example Editor state slice containing fields related to the Manual Editor — Response Example Editor.
 */
export const createResponseExampleEditorSlice: StateCreator<
  APIDesignerStore & ResponseExampleEditorSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  ResponseExampleEditorSlice
> = (set, get) => ({
  responseExampleEditor: {
    ...initialState,

    /**
     * Add a new response example to an existing status code and MIME type.
     * Since we already have both, we only need to handle adding examples to existing content.
     *
     */
    addResponseExample: (statusCode: string, specificMimeType: string) => {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();
      const newOperation = structuredClone(operation);

      // https://spec.openapis.org/oas/v3.1.0#response-object
      const currentResponseObject = operation?.responses?.[statusCode] as OpenAPIV3.ResponseObject | undefined;

      // Ensuring the example name is unique
      const baseExampleLabel = codes[statusCode][0];
      let newExampleCount = 1;
      let newExampleLabel = baseExampleLabel;
      const currentExampleLabels = Object.keys(currentResponseObject?.content?.[specificMimeType]?.examples || {});

      while (currentExampleLabels.includes(newExampleLabel)) {
        newExampleLabel = `${baseExampleLabel} - ${newExampleCount}`;
        newExampleCount += 1;
      }

      const newExampleValue = {
        summary: newExampleLabel,
        value: '{}',
      };

      const hasExampleObject = !!currentResponseObject?.content?.[specificMimeType]?.example;
      const hasExamplesObject = !!currentResponseObject?.content?.[specificMimeType]?.examples;

      // Only create new response if the response object doesn't exist at all
      if (!currentResponseObject) {
        newOperation!.responses![statusCode] = {
          content: {
            [specificMimeType]: {
              schema: createEmptySchema(),
              examples: {
                [newExampleLabel]: newExampleValue,
              },
            },
          },
          description: codes[statusCode]?.[0] || `Response ${statusCode}`,
        };

        set(
          state => {
            state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
            state.responseExampleEditor.lastEditedExample = {
              id: `${statusCode}_${newExampleLabel}`,
              mediaType: specificMimeType,
              namedId: newExampleLabel,
              status: statusCode,
            };
            state.bodySchemaEditor.openedStatusCode = statusCode;
          },
          false,
          actionLog('adding response example'),
        );
        return newExampleLabel;
      }

      // Handle the case where we need to convert from 'example' to 'examples'
      if (hasExampleObject && !hasExamplesObject) {
        const oldExample = currentResponseObject?.content?.[specificMimeType]?.example;

        const response = newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject;
        if (!response.content) {
          response.content = {};
        }
        if (!response.content[specificMimeType]) {
          response.content[specificMimeType] = {};
        }
        response.content[specificMimeType].examples = {
          [`exampleFromExisting${statusCode}`]: {
            value: oldExample,
          },
          [newExampleLabel]: newExampleValue,
        };
        delete response.content[specificMimeType].example;
      } else {
        // Add to existing examples or create examples object
        const response = newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject;
        if (!response.content) {
          response.content = {};
        }
        if (!response.content[specificMimeType]) {
          response.content[specificMimeType] = {};
        }
        // Preserve existing examples when adding new one
        const existingExamples = response.content[specificMimeType].examples || {};
        response.content[specificMimeType].examples = {
          ...existingExamples,
          [newExampleLabel]: newExampleValue,
        };
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${statusCode}_${newExampleLabel}`,
            mediaType: specificMimeType,
            namedId: newExampleLabel,
            status: statusCode,
          };
          state.bodySchemaEditor.openedStatusCode = statusCode;
        },
        false,
        actionLog('adding response example'),
      );

      return newExampleLabel;
    },

    changeContentType: (newContentType: string, oldContentType: string, statusCode: string, namedId: string) => {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();
      const newOperation = structuredClone(operation);

      // find the example in the old content type under content.[oldContentType].examples
      const response = newOperation?.responses?.[statusCode] as OpenAPIV3.ResponseObject;
      const oldExamples = response?.content?.[oldContentType]?.examples || {};
      const exampleNames = Object.keys(oldExamples);
      const exampleIndex = exampleNames.findIndex(exampleName => exampleName === namedId);

      if (exampleIndex === -1) {
        // Create an example with existing values if not found
        const existingValue = response?.content?.[oldContentType]?.example || '';
        if (!response?.content?.[newContentType]?.examples) {
          (response?.content?.[newContentType] as OpenAPIV3.MediaTypeObject).examples = {};
        }
        (response?.content?.[newContentType] as OpenAPIV3.MediaTypeObject).examples![namedId] = {
          summary: namedId,
          value: existingValue,
        };
      } else {
        // move the example to the new content type
        const exampleToMove = oldExamples[namedId];
        delete oldExamples[namedId];
        if (!response?.content?.[newContentType]?.examples) {
          (response?.content?.[newContentType] as OpenAPIV3.MediaTypeObject).examples = {};
        }
        (response?.content?.[newContentType] as OpenAPIV3.MediaTypeObject).examples![namedId] = exampleToMove;

        // clean up empty examples objects only
        if (Object.keys(oldExamples).length === 0) {
          delete response?.content?.[oldContentType]?.examples;
        }
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${statusCode}_${namedId}`,
            mediaType: newContentType,
            namedId,
            status: statusCode,
          };
        },
        false,
        actionLog('changeContentType'),
      );

      return { statusCode, namedId, contentType: newContentType };
    },

    deleteResponseExample: (statusCode: string, mediaType: string, namedId: string) => {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();
      const newOperation = structuredClone(operation);

      // Only delete the specific example, never delete media types or response objects
      const responseObject = newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject;
      if (responseObject?.content?.[mediaType]?.examples) {
        delete responseObject.content![mediaType]!.examples![namedId as string];

        // If this was the last example, delete the entire examples property
        if (Object.keys(responseObject.content![mediaType]!.examples!).length === 0) {
          delete responseObject.content![mediaType]!.examples;
        }
      } else if (responseObject?.content?.[mediaType]?.example) {
        delete responseObject.content![mediaType]!.example;
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${statusCode}_${namedId}`,
            mediaType,
            namedId,
            status: statusCode,
          };
        },
        false,
        actionLog('deleteResponseExample'),
      );

      set(() => {}, false, actionLog('setting selected response example to first available example'));
    },

    generateResponseSchema: args => {
      const { statusCode, mediaType, namedId } = args;

      if (mediaType.includes('xml')) {
        throw new Error('Generating schema from XML response examples is not supported');
      }

      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      if (!operation) {
        throw new Error('No operation found');
      }

      const { code, namedId: exampleNameId } = get().responseExampleEditor.getExampleCode(
        statusCode,
        mediaType,
        namedId,
      );

      const parsedExample = parseResponseExampleJSON(code);

      /**
       * NOTE:
       * we cannot use safelyParseJSON here because it will return null if the JSON is invalid
       * and we want to know what the error is
       *
       * we also use a special key of '__isParseError' to distinguish between a valid JSON object and an
       * invalid JSON object, we use the '__isParseError' because there is a chance of a valid
       * JSON object to contain both the 'error' and 'value' keys, an edge case might still occur where the
       * JSON legitimately contains a '__isParseError' key, but is unlikely
       */
      if (parsedExample && typeof parsedExample === 'object' && '__isParseError' in parsedExample) {
        set(state => {
          state.responseExampleEditor.openedExample = {
            statusCode,
            contentType: mediaType,
            namedId: exampleNameId || '',
            error: parsedExample.error,
          };
        });
        throw new Error(parsedExample.error);
      }

      // TODO: adjust this function to also create a schema from an XML object, currently it only creates a schema from a JSON object
      const generatedSchema = generateJSONSchemaFromObject(parsedExample);

      const newOperation = structuredClone(operation);

      newOperation.responses ??= {};
      newOperation.responses[statusCode] ??= {
        description: codes[statusCode]?.[0] || 'Response',
        content: {},
      };

      const responseObject = newOperation.responses[statusCode] as OpenAPIV3.ResponseObject;
      responseObject.content ??= {};
      responseObject.content[mediaType] ??= { schema: createEmptySchema(true) };

      responseObject.content[mediaType].schema = generatedSchema as OpenAPIV3.SchemaObject;

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.bodySchemaEditor.openedStatusCode = statusCode;
        },
        false,
        actionLog('generateResponseSchema', { statusCode, mediaType }),
      );
    },

    getAllExampleNames: () => {
      const schema = get().apiObject?.schema;
      const currentPath = get().apiObject?.path as string;
      const currentMethod = get().apiObject?.method as HttpMethods;

      if (!schema || !currentPath || !currentMethod) {
        throw new Error('Required API object properties are missing');
      }

      // Create Oas instance to use its methods
      const oas = new Oas(schema);

      // checks for the .examples object first and then fallback to schema.example
      const operation = oas.operation(currentPath, currentMethod);
      const examples = operation.getResponseExamples();
      const statusMap = new Map<string, Map<string, string[]>>();

      examples.forEach(example => {
        const mediaTypeMap = new Map<string, string[]>();

        Object.entries(example.mediaTypes).forEach(([mediaType, mediaTypeExamples]) => {
          if (isTextBasedMIMEType(mediaType)) {
            if (mediaTypeExamples && mediaTypeExamples.length > 0) {
              const exampleNames = mediaTypeExamples
                .map(ex => ex.title || ex.summary) // fallback to summary if title is not present
                .filter((name): name is string => Boolean(name));
              mediaTypeMap.set(
                mediaType,
                exampleNames.length > 0 ? exampleNames : [`${codes[example.status][0]} - ${mediaType}`],
              );
            } else {
              mediaTypeMap.set(mediaType, [`${codes[example.status][0]} - ${mediaType}`]);
            }
          } else {
            const exampleNames = mediaTypeExamples
              .map(ex => ex.title || ex.summary) // fallback to summary if title is not present
              .filter((name): name is string => Boolean(name));

            mediaTypeMap.set(mediaType, exampleNames);
          }
        });

        statusMap.set(example.status, mediaTypeMap);
      });

      return statusMap;
    },

    getAllValidStatusCodes: () => {
      const { operation } = get().getCurrentOperation();
      const statusCodes = Object.keys(operation?.responses || {});

      return statusCodes.filter(statusCode => {
        const responseObject = operation?.responses?.[statusCode] as OpenAPIV3.ResponseObject | undefined;
        return responseObject?.content && Object.keys(responseObject.content).length > 0;
      });
    },

    getFirstExample: () => {
      const { currentPath, currentMethod } = get().getCurrentOperation();

      const oas = new Oas(get().apiObject?.schema || '{}');
      const operation = oas.operation(currentPath, currentMethod);

      const firstRawExample = operation?.getResponseExamples()[0];
      const firstStatus = firstRawExample?.status || '';
      const firstMediaType = firstRawExample ? Object.keys(firstRawExample.mediaTypes)[0] : '';
      const firstNamedId = firstMediaType ? firstRawExample.mediaTypes[firstMediaType][0]?.summary : '';

      return {
        id: firstNamedId ? firstStatus.concat('_', firstNamedId) : firstStatus,
        mediaType: firstMediaType,
        namedId: firstNamedId,
        status: firstStatus,
      };
    },

    getOperationResponseProperties: (contentType: string, statusCode: string) => {
      const { operation } = get().getCurrentOperation();

      // https://spec.openapis.org/oas/v3.1.0#responses-object
      const hasResponseObject = !!operation?.responses || false;

      // https://spec.openapis.org/oas/v3.1.0#response-object
      const responseObject = operation?.responses?.[statusCode] as OpenAPIV3.ResponseObject | undefined;

      const hasStatusCode = !!responseObject || false;
      const hasContentType = !!responseObject?.content?.[contentType] || false;
      const hasCustomDescription = responseObject?.description !== codes[statusCode][0];
      const hasMultipleContentTypes = Object.keys(responseObject?.content || {}).length > 1;

      // https://spec.openapis.org/oas/v3.1.0#media-type-object
      const mediaTypeObject = responseObject?.content?.[contentType];

      const hasExamplesObject = !!mediaTypeObject?.examples || false;
      const hasMultipleExamples = Object.keys(mediaTypeObject?.examples || {}).length > 1;

      return {
        hasResponseObject,
        hasStatusCode,
        hasContentType,
        hasCustomDescription,
        hasMultipleContentTypes,
        hasExamplesObject,
        hasMultipleExamples,
      };
    },

    getExampleCode: (statusCode: string, mediaType: string, namedId?: string) => {
      const responseObject = get().getCurrentOperation()?.operation?.responses?.[
        statusCode
      ] as OpenAPIV3.ResponseObject;

      let currentCode = '';
      let codeNameId = '';

      if (namedId) {
        const exampleFromExamples = responseObject?.content?.[mediaType]?.examples?.[
          namedId as string
        ] as OpenAPIV3.ExampleObject;
        if (exampleFromExamples?.value) {
          currentCode = exampleFromExamples.value;
          codeNameId = namedId as string;
        }
      } else {
        const examples = responseObject?.content?.[mediaType]?.examples;
        if (examples) {
          const firstExampleKey = Object.keys(examples)[0];
          if (firstExampleKey) {
            const firstExample = examples[firstExampleKey] as OpenAPIV3.ExampleObject;
            if (firstExample?.value) {
              currentCode = firstExample.value;
              codeNameId = firstExampleKey;
            }
          }
        }
      }

      // If no examples object or no value, try schema.example for text-based media types
      if (!currentCode && isTextBasedMIMEType(mediaType)) {
        const schemaExample = (responseObject?.content?.[mediaType]?.schema as OpenAPIV3.SchemaObject)?.example;
        if (schemaExample) {
          currentCode = schemaExample;
          codeNameId = namedId || '';
        }
      }

      if (typeof currentCode === 'object') {
        return { code: safelyStringifyJSON(currentCode) || '', namedId: codeNameId };
      }

      // Return empty string if no example found
      return { code: currentCode, namedId: codeNameId };
    },

    initialize: () => {
      set(
        state => {
          state.responseExampleEditor.lastEditedExample = state.responseExampleEditor.getFirstExample();
          return state;
        },
        false,
        actionLog('initialize response example editor slice'),
      );
    },

    updateResponseExampleName(exampleName: string, statusCode: string, mediaType: string, namedId: string) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();

      const newOperation = structuredClone(operation);

      if (!newOperation!.responses![statusCode]) {
        // Should never get here, but making ts happy
        throw new Error('Cannot find selected example in response object.');
      }

      // Casting the response object to an OpenAPIV3.ResponseObject since we know it's not a $ref
      const responseObject = newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject;

      // For text-based media types, ensure examples object exists
      if (isTextBasedMIMEType(mediaType) && !responseObject.content![mediaType]?.examples) {
        if (!responseObject.content![mediaType]) {
          responseObject.content![mediaType] = {};
        }
        responseObject.content![mediaType].examples = {};

        // Create a default example with the namedId if it doesn't exist
        if (!responseObject.content![mediaType].examples![namedId]) {
          // Try to get the original value from schema.example or example property
          let originalValue = '';
          const schemaExample = (responseObject.content![mediaType].schema as OpenAPIV3.SchemaObject)?.example;
          const singleExample = responseObject.content![mediaType].example;

          if (schemaExample !== undefined) {
            originalValue = schemaExample;
          } else if (singleExample !== undefined) {
            originalValue = singleExample;
          }

          responseObject.content![mediaType].examples![namedId] = {
            summary: namedId,
            value: originalValue,
          };
        }
      }

      if (!responseObject.content![mediaType] || !namedId || !responseObject.content![mediaType]!.examples![namedId]) {
        // Should never get here, but making ts happy
        throw new Error('Cannot find selected example in response object.');
      }

      // Make sure the example doesn't overwrite any existing ones
      // If there is already an example with this name, we'll append a number to the end of the name until we find a unique name
      let newExampleName = exampleName;
      let exampleIndex = 1;
      while (responseObject.content![mediaType]!.examples![newExampleName]) {
        newExampleName = `${exampleName} ${exampleIndex}`;
        exampleIndex += 1;
      }

      // Casting the example object to an OpenAPIV3.ExampleObject since we know it's not a $ref
      const newExample = responseObject.content![mediaType]!.examples![namedId] as OpenAPIV3.ExampleObject;
      newExample.summary = newExampleName;
      responseObject.content![mediaType]!.examples![newExampleName] = newExample;

      delete responseObject.content![mediaType]!.examples![namedId];

      newOperation!.responses![statusCode] = responseObject;
      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${statusCode}_${newExampleName}`,
            mediaType,
            namedId: newExampleName,
            status: statusCode,
          };
        },
        false,
        actionLog('updateResponseExampleName'),
      );
    },

    updateSnippet(value, statusCode, mediaType, namedId) {
      const { operation, currentPath, currentMethod } = get().getCurrentOperation();
      const newOperation = structuredClone(operation);

      let currentValue: $TSFixMe = value;

      if (mediaType === 'application/json') {
        try {
          currentValue = JSON.parse(value);
        } catch (e) {
          /* empty */
        }
      }

      // https://spec.openapis.org/oas/v3.1.0#response-object
      const responseObject = operation!.responses![statusCode] as OpenAPIV3.ResponseObject;

      const hasExamplesObject = !!responseObject?.content?.[mediaType]?.examples;
      const hasExampleObject = !!responseObject?.content?.[mediaType]?.example;

      if (isTextBasedMIMEType(mediaType) && !hasExamplesObject) {
        if (!(newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType]) {
          (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType] = {};
        }
        (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].examples = {};
      }

      let code;
      if (hasExamplesObject) {
        code = (responseObject?.content?.[mediaType]?.examples?.[namedId as string] as OpenAPIV3.ExampleObject)?.value;
      } else if (hasExampleObject && !isTextBasedMIMEType(mediaType)) {
        code = responseObject?.content?.[mediaType]?.example;
      }

      // note: we must check manually for undefined and null because the code can be an empty string, doing `if (code)` will essentially make empty examples uneditable
      if (code !== undefined && code !== null) {
        if (hasExamplesObject) {
          (
            (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].examples![
              namedId as string
            ] as OpenAPIV3.ExampleObject
          ).value = currentValue;
        } else if (hasExampleObject && !isTextBasedMIMEType(mediaType)) {
          (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].example = currentValue;
        }
      }

      if (isTextBasedMIMEType(mediaType)) {
        if (
          !(newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].examples![
            namedId as string
          ]
        ) {
          (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].examples![
            namedId as string
          ] = {
            summary: namedId,
            value: currentValue,
          };
        } else {
          (
            (newOperation!.responses![statusCode] as OpenAPIV3.ResponseObject).content![mediaType].examples![
              namedId as string
            ] as OpenAPIV3.ExampleObject
          ).value = currentValue;
        }
      }

      set(
        state => {
          state.apiObject!.schema!.paths![currentPath]![currentMethod] = newOperation;
          state.responseExampleEditor.lastEditedExample = {
            id: `${statusCode}_${namedId}`,
            mediaType,
            namedId,
            status: statusCode,
          };
          // Clear error if it exists for this example, but keep it open
          if (
            state.responseExampleEditor.openedExample?.statusCode === statusCode &&
            state.responseExampleEditor.openedExample?.contentType === mediaType &&
            state.responseExampleEditor.openedExample?.namedId === namedId
          ) {
            state.responseExampleEditor.openedExample.error = undefined;
          }
        },
        false,
        actionLog('updateSnippet', { value, statusCode, mediaType, namedId }),
      );
    },

    setOpenedExample: (example: { statusCode: string; contentType: string; namedId: string } | null) => {
      set(
        state => {
          state.responseExampleEditor.openedExample = example;
        },
        false,
        actionLog('setOpenedExample', { example }),
      );
    },

    getResponseExampleNames: (statusCode: string, mediaType: string) => {
      const { operation } = get().getCurrentOperation();
      const responseObject = operation?.responses?.[statusCode] as OpenAPIV3.ResponseObject;

      if (!responseObject?.content?.[mediaType]) {
        return null;
      }

      const mediaTypeObject = responseObject.content[mediaType];
      const exampleNames: string[] = [];

      // Check for 'examples' object (multiple examples)
      if (mediaTypeObject.examples) {
        const exampleKeys = Object.keys(mediaTypeObject.examples);
        exampleNames.push(...exampleKeys);
      }

      // Check for 'example' property (single example)
      if (mediaTypeObject.example !== undefined) {
        exampleNames.push('Default Example');
      }

      // For text-based media types, also check schema.example
      if (isTextBasedMIMEType(mediaType)) {
        const schemaExample = (mediaTypeObject.schema as OpenAPIV3.SchemaObject)?.example;
        if (schemaExample !== undefined && !exampleNames.includes('Default Example')) {
          exampleNames.push('Schema Example');
        }
      }

      return exampleNames.length > 0 ? exampleNames : null;
    },
  },
});
